import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Heading, Paragraph, Textfield } from '@digdir/designsystemet-react';
import { DateUtils } from '../../utils/DateUtils';
import classes from './SurveyDeadlineDialog.module.css';

type SurveyDeadlineDialogProps = {
  open: boolean;
  surveyTitle: string;
  createdAt: Date;
  currentDeadlineInDays: number;
  isSubmitting?: boolean;
  onSubmit: (deadlineInDays: number) => void;
  onClose: () => void;
};

/** Endre svarfristen (antall dager fra opprettet dato) for en pågående undersøkelse. */
export const SurveyDeadlineDialog = ({
  open,
  surveyTitle,
  createdAt,
  currentDeadlineInDays,
  isSubmitting = false,
  onSubmit,
  onClose,
}: SurveyDeadlineDialogProps) => {
  const { t } = useTranslation();
  const [deadlineInDays, setDeadlineInDays] = useState(currentDeadlineInDays);
  const newDeadline = DateUtils.getDatePlusDays(new Date(createdAt), deadlineInDays);
  const isInvalid = deadlineInDays <= 0 || !DateUtils.DateIsAfterToday(newDeadline);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      closedby="any"
      closeButton={t('surveys.page.closeDialog')}
      className={classes.dialog}
    >
      <form
        className={classes.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (!isInvalid) onSubmit(deadlineInDays);
        }}
      >
        <Heading level={2} data-size="sm">
          {t('surveys.surveyDeadlineDialog.title', { surveyName: surveyTitle })}
        </Heading>
        <Textfield
          label={t('surveys.surveyDeadlineDialog.fieldLabel')}
          description={t('surveys.surveyDeadlineDialog.fieldDescription')}
          inputMode="numeric"
          value={deadlineInDays}
          onChange={(event) => setDeadlineInDays(Number(event.target.value) || 0)}
          error={isInvalid ? t('surveys.surveyDeadlineDialog.errorMessage') : undefined}
          className={classes.field}
        />
        <Paragraph data-size="sm" className={classes.info}>
          {t('surveys.surveyDeadlineDialog.deadlineInfo', {
            date: DateUtils.getShortDateFromDate(newDeadline, true),
          })}
        </Paragraph>
        <div className={classes.buttons}>
          <Button type="submit" disabled={isInvalid || isSubmitting}>
            {t('surveys.surveyDeadlineDialog.buttons.update')}
          </Button>
          <Button type="button" variant="tertiary" onClick={onClose}>
            {t('surveys.surveyDeadlineDialog.buttons.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
